import {
  AcademicCapIcon as AcademicCapIconOutline,
  Cog6ToothIcon as Cog6ToothIconOutline,
  DocumentChartBarIcon as DocumentChartBarIconOutline,
  RocketLaunchIcon as RocketLaunchIconOutline,
  SparklesIcon as SparklesIconOutline
} from '@heroicons/react/24/outline';
import {
  AcademicCapIcon,
  Cog6ToothIcon,
  DocumentChartBarIcon,
  RocketLaunchIcon,
  SparklesIcon
} from '@heroicons/react/24/solid';
import { useKeycloak } from '@react-keycloak/web';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { CMultifundFactsheetRoles } from '../../constants/CMultifundFactsheetRoles';
import { EUserRoles } from '../../enums/EUserRoles';
import { PageRoutes } from '../../enums/enums';
import { useAppSelector } from '../../hooks/hooks';
import { userHasRole } from '../../utils/utils';
import CLocalizedText from '../CLocalizedText';
import CLocalizedTextStringStatic from '../CLocalizedTextStringStatic';
import CSidebarGroup from '../CSidebarGroup';
import CSidebarButtonLink from './CSidebarButtonLink';

export default function SidebarContents() {
  const path = useLocation().pathname.split('/');
  const { keycloak } = useKeycloak();
  const { dashboard } = useAppSelector((state) => state);

  const userAccountsLabel = CLocalizedTextStringStatic('sidebar.userAccounts');
  const internalAreaLabel = CLocalizedTextStringStatic('sidebar.internalAreaHeading');
  const singleStockLabel = CLocalizedTextStringStatic('sidebar.singleStock');
  const blacScholesCalculator = CLocalizedTextStringStatic('sidebar.blackScholesCalculator');

  return (
    <>
      <CSidebarButtonLink
        to={PageRoutes.NEWS_OUTLET}
        isActive={[...path][1] === PageRoutes.NEWS_OUTLET}
        startIcon={{ default: <SparklesIconOutline />, selected: <SparklesIcon /> }}
      >
        <CLocalizedText dictKey={'sidebar.news'} />
      </CSidebarButtonLink>
      {keycloak.authenticated &&
      userHasRole(keycloak.tokenParsed, CMultifundFactsheetRoles) &&
      dashboard.funds.length > 0 ? (
        <CSidebarGroup
          title={'Factsheet'}
          startIcon={{ default: <DocumentChartBarIconOutline />, selected: <DocumentChartBarIcon /> }}
          groupIsActive={[PageRoutes.PUBLIC_FACTSHEET_COLLECTION_OUTLET, PageRoutes.FACTSHEET_OUTLET]
            .map((e) => e.toString())
            .includes([...path][1] ?? '')}
        >
          <>
            {dashboard.funds.map((fund) => (
              <CSidebarButtonLink
                key={fund.id}
                to={
                  fund.id === 'nusbos'
                    ? PageRoutes.PUBLIC_FACTSHEET_COLLECTION_OUTLET
                    : `${PageRoutes.FACTSHEET_OUTLET}/${fund.id}`
                }
                isActive={
                  fund.id === 'nusbos'
                    ? [...path][1] === PageRoutes.PUBLIC_FACTSHEET_COLLECTION_OUTLET
                    : [...path][1] === PageRoutes.FACTSHEET_OUTLET && [...path][2] === fund.id
                }
                indent
              >
                {fund.shortName}
              </CSidebarButtonLink>
            ))}
          </>
        </CSidebarGroup>
      ) : (
        <CSidebarButtonLink
          to={`${PageRoutes.PUBLIC_FACTSHEET_COLLECTION_OUTLET}`}
          isActive={[...path][1] === PageRoutes.PUBLIC_FACTSHEET_COLLECTION_OUTLET}
          startIcon={{ default: <DocumentChartBarIconOutline />, selected: <DocumentChartBarIcon /> }}
        >
          Factsheet
        </CSidebarButtonLink>
      )}

      {userHasRole(keycloak.tokenParsed, [[EUserRoles.STAFF]]) && (
        <div className={'font-bold uppercase mx-2 mt-2 border-t-2 border-white/20 mb-2'}>
          {dashboard.sidebar.isExpanded && <p className={'pt-6 text-xs'}>{internalAreaLabel}</p>}
        </div>
      )}

      {userHasRole(keycloak.tokenParsed, [[EUserRoles.STAFF]]) && dashboard.funds.length > 0 && (
        <CSidebarGroup
          groupIsActive={[...path][1] === PageRoutes.OPS_NEWS_OUTLET}
          title={CLocalizedTextStringStatic('sidebar.pm')}
          startIcon={{ default: <RocketLaunchIconOutline />, selected: <RocketLaunchIcon /> }}
        >
          {dashboard.funds.map((fund) => (
            <CSidebarButtonLink
              key={fund.id}
              to={`/${PageRoutes.OPS_NEWS_OUTLET}/${fund.id}`}
              isActive={[...path][1] === PageRoutes.OPS_NEWS_OUTLET && [...path][2] === fund.id}
              indent
            >
              {fund.shortName}
            </CSidebarButtonLink>
          ))}
        </CSidebarGroup>
      )}

      {userHasRole(keycloak.tokenParsed, [[EUserRoles.STAFF]]) && (
        <CSidebarGroup
          title={'Topics'}
          startIcon={{ default: <AcademicCapIconOutline />, selected: <AcademicCapIcon /> }}
          groupIsActive={[PageRoutes.SINGLE_STOCK_OUTLET, PageRoutes.CALCULATOR_OUTLET]
            .map((e) => e.toString())
            .includes([...path][1] ?? '')}
        >
          <>
            <CSidebarButtonLink
              to={PageRoutes.SINGLE_STOCK_OUTLET}
              isActive={[...path][1] === PageRoutes.SINGLE_STOCK_OUTLET}
              indent
            >
              {singleStockLabel}
            </CSidebarButtonLink>

            <CSidebarButtonLink
              to={PageRoutes.SECTOR_PERFORMANCE_OUTLET}
              isActive={[...path][1] === PageRoutes.SECTOR_PERFORMANCE_OUTLET}
              indent
            >
              <CLocalizedText dictKey={'sectorPerformance.route'} />
            </CSidebarButtonLink>

            <CSidebarButtonLink
              to={PageRoutes.CALCULATOR_OUTLET}
              isActive={[...path][1] === PageRoutes.CALCULATOR_OUTLET}
              indent
            >
              {blacScholesCalculator}
            </CSidebarButtonLink>
          </>
        </CSidebarGroup>
      )}

      {userHasRole(keycloak.tokenParsed, [[EUserRoles.STAFF]]) && (
        <CSidebarGroup
          groupIsActive={[
            PageRoutes.PUBLISHING_OUTLET,
            PageRoutes.USERS_OUTLET,
            PageRoutes.CACHE_MANAGEMENT_OUTLET,
            PageRoutes.USAGE_TRACKING_OUTLET,
            PageRoutes.DOWNLOAD_CENTER_OUTLET,
            PageRoutes.I18N_CENTER_OUTLET,
            PageRoutes.TRADE_ORDERS_OUTLET
          ]
            .map((e) => e.toString())
            .includes([...path][1] ?? '')}
          title={'Admin Console'}
          startIcon={{ default: <Cog6ToothIconOutline />, selected: <Cog6ToothIcon /> }}
        >
          <CSidebarButtonLink
            to={PageRoutes.PUBLISHING_OUTLET}
            isActive={[...path][1] === PageRoutes.PUBLISHING_OUTLET}
            indent
          >
            <CLocalizedText dictKey={'globalSidebarPublications'} />
          </CSidebarButtonLink>

          <>
            {userHasRole(keycloak.tokenParsed, [[EUserRoles.STAFF]]) && (
              <CSidebarButtonLink
                to={PageRoutes.USERS_OUTLET}
                isActive={[...path][1] === PageRoutes.USERS_OUTLET}
                indent
              >
                {userAccountsLabel}
              </CSidebarButtonLink>
            )}
          </>

          <>
            {userHasRole(keycloak.tokenParsed, [[EUserRoles.ADMIN]]) && (
              <CSidebarButtonLink
                to={PageRoutes.CACHE_MANAGEMENT_OUTLET}
                isActive={[...path][1] === PageRoutes.CACHE_MANAGEMENT_OUTLET}
                indent
              >
                <CLocalizedText dictKey={'cacheManagement.title'} />
              </CSidebarButtonLink>
            )}
          </>

          <>
            {userHasRole(keycloak.tokenParsed, [[EUserRoles.STAFF]]) && (
              <CSidebarButtonLink
                to={PageRoutes.USAGE_TRACKING_OUTLET}
                isActive={[...path][1] === PageRoutes.USAGE_TRACKING_OUTLET}
                indent
              >
                <CLocalizedText dictKey={'usageTracking.title'} />
              </CSidebarButtonLink>
            )}
          </>

          <>
            {userHasRole(keycloak.tokenParsed, [[EUserRoles.STAFF]]) && (
              <CSidebarButtonLink
                to={PageRoutes.DOWNLOAD_CENTER_OUTLET}
                isActive={[...path][1] === PageRoutes.DOWNLOAD_CENTER_OUTLET}
                indent
              >
                <CLocalizedText dictKey={'downloadCenter.title'} />
              </CSidebarButtonLink>
            )}
          </>

          <>
            {userHasRole(keycloak.tokenParsed, [[EUserRoles.ADMIN]]) && (
              <CSidebarButtonLink
                to={PageRoutes.I18N_CENTER_OUTLET}
                isActive={[...path][1] === PageRoutes.I18N_CENTER_OUTLET}
                indent
              >
                <CLocalizedText dictKey={'i18n.heading.title'} />
              </CSidebarButtonLink>
            )}
          </>
        </CSidebarGroup>
      )}
    </>
  );
}
